import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { DsRadioComponent } from './ds-radio.component';

export type DsRadioGroupOrientation = 'horizontal' | 'vertical';
export type DsRadioGroupState = 'default' | 'error';

@Component({
  selector: 'ds-radio-group',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DsRadioGroupComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DsRadioGroupComponent),
      multi: true,
    },
  ],
  template: `
    <div
      class="ds-radio-group"
      role="radiogroup"
      [class.ds-radio-group--disabled]="effectiveDisabled"
      [class.ds-radio-group--error]="state === 'error'"
      [attr.aria-label]="label ? null : ariaLabel"
      [attr.aria-labelledby]="label ? labelId : null"
      [attr.aria-describedby]="helpText ? helpId : null"
      [attr.aria-disabled]="effectiveDisabled"
      [attr.aria-invalid]="state === 'error'"
      [attr.aria-required]="required"
    >
      @if (label) {
        <div class="ds-radio-group__label" [attr.id]="labelId">
          {{ label }}
          @if (required) {
            <span aria-hidden="true">*</span>
          }
        </div>
      }

      <div
        class="ds-radio-group__options"
        [class.ds-radio-group__options--horizontal]="orientation === 'horizontal'"
      >
        <ng-content />
      </div>

      @if (helpText) {
        <div
          class="ds-radio-group__help"
          [attr.id]="helpId"
          [attr.role]="state === 'error' ? 'alert' : null"
        >
          {{ helpText }}
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }

    .ds-radio-group {
      display: grid;
      gap: var(--ds-radio-group-gap);
    }

    .ds-radio-group__label {
      color: var(--ds-radio-group-label-color);
      font-size: var(--ds-radio-group-label-font-size);
      font-weight: var(--ds-radio-group-label-font-weight);
      line-height: var(--ds-radio-group-label-line-height);
    }

    .ds-radio-group__options {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--ds-radio-group-options-gap);
    }

    .ds-radio-group__options--horizontal {
      flex-direction: row;
      flex-wrap: wrap;
    }

    .ds-radio-group__help {
      color: var(--ds-radio-group-help-color);
      font-size: var(--ds-radio-group-help-font-size);
      font-weight: var(--ds-radio-group-help-font-weight);
      line-height: var(--ds-radio-group-help-line-height);
    }

    .ds-radio-group--error .ds-radio-group__help {
      color: var(--ds-radio-group-error-color);
    }

    .ds-radio-group--disabled .ds-radio-group__label,
    .ds-radio-group--disabled .ds-radio-group__help {
      opacity: calc(var(--ds-radio-group-disabled-opacity) / 100px);
    }
  `],
})
export class DsRadioGroupComponent implements AfterContentInit, ControlValueAccessor, OnChanges, OnDestroy, Validator {
  private static counter = 0;

  private readonly instanceId = ++DsRadioGroupComponent.counter;
  readonly helpId = `ds-radio-group-${this.instanceId}-help`;
  readonly labelId = `ds-radio-group-${this.instanceId}-label`;

  @Input() ariaLabel = 'Opciones';
  @Input() disabled = false;
  @Input() helpText = '';
  @Input() label = '';
  @Input() name = `ds-radio-group-${this.instanceId}`;
  @Input() orientation: DsRadioGroupOrientation = 'vertical';
  @Input() required = false;
  @Input() state: DsRadioGroupState = 'default';
  @Input() value: string | null = null;

  @Output() valueChange = new EventEmitter<string | null>();

  @ContentChildren(DsRadioComponent, { descendants: true })
  private radioOptions?: QueryList<DsRadioComponent>;

  private contentChangesSubscription?: Subscription;
  private formDisabled = false;
  private optionSubscriptions = new Subscription();
  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  get effectiveDisabled(): boolean {
    return this.disabled || this.formDisabled;
  }

  ngAfterContentInit(): void {
    this.bindOptions();
    this.contentChangesSubscription = this.radioOptions?.changes.subscribe(() => this.bindOptions());
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.syncOptions();
    this.onValidatorChange();
  }

  ngOnDestroy(): void {
    this.contentChangesSubscription?.unsubscribe();
    this.optionSubscriptions.unsubscribe();
  }

  writeValue(value: unknown): void {
    this.value = typeof value === 'string' ? value : null;
    this.syncOptions();
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
    this.syncOptions();
    this.changeDetectorRef.markForCheck();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const isEmpty = control.value === null || control.value === undefined || control.value === '';
    return this.required && !control.disabled && isEmpty ? { required: true } : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  private bindOptions(): void {
    this.optionSubscriptions.unsubscribe();
    this.optionSubscriptions = new Subscription();

    this.radioOptions?.forEach((option) => {
      this.optionSubscriptions.add(option.selectedChange.subscribe(() => this.selectOption(option)));
      this.optionSubscriptions.add(option.blurred.subscribe(() => this.onTouched()));
    });

    this.syncOptions();
  }

  private selectOption(option: DsRadioComponent): void {
    if (this.effectiveDisabled) return;

    this.value = option.value;
    this.syncOptions();
    this.valueChange.emit(this.value);
    this.onChange(this.value);
    this.changeDetectorRef.markForCheck();
  }

  private syncOptions(): void {
    const describedBy = this.helpText ? this.helpId : null;

    this.radioOptions?.forEach((option) => {
      option.setGroupState({
        ariaDescribedBy: describedBy,
        disabled: this.effectiveDisabled,
        name: this.name,
        required: this.required,
        selected: option.value === this.value,
      });
    });
  }
}
